import React from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  InputAdornment, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { FORMATS_QUILL, ORIGINES, OUTILS_QUILL, TVA } from './constantes';
import { themeExpert } from '../../theme/expert';

/** Les quatre colonnes d'argent d'un poste, et ce que chacune veut dire. */
const MONTANTS = [
  { champ: 'reclamer', libelle: 'Réclamé',
    aide: "Ce que l'assuré demande — le devis, s'il en a un." },
  { champ: 'calculer', libelle: 'Calculé',
    aide: 'Votre chiffrage. Il se remplit seul depuis les lignes de détail.' },
  { champ: 'accorder', libelle: 'Assuré',
    aide: 'Ce que la compagnie prend en charge.' },
  { champ: 'recours', libelle: 'Recours',
    aide: "Ce qui sera réclamé au tiers responsable." },
];

export default function PosteDialog({ ouvert, onFermer, poste, setPoste,
                                      onEnregistrer, occupe }) {
  if (!poste) return null;

  const poser = (champ) => (e) =>
    setPoste((etat) => ({ ...etat, [champ]: e.target.value }));

  return (
    <ThemeProvider theme={themeExpert}>
      <Dialog open={ouvert} onClose={onFermer} fullWidth maxWidth="md">
        <Box component="form" onSubmit={onEnregistrer} noValidate>
          <DialogTitle>
            {poste.id ? 'Modifier le poste' : 'Ajouter un poste'}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2.5}>
              <TextField label="Titre du poste" value={poste.title || ''}
                         onChange={poser('title')} required autoFocus
                         helperText="Ex. : réfection de la peinture du salon." />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField select label="Origine" value={poste.origin || '1'}
                           onChange={poser('origin')}
                           helperText="Sous quelle rubrique ce poste sera classé.">
                  {ORIGINES.map(({ valeur, libelle }) => (
                    <MenuItem key={valeur} value={valeur}>{libelle}</MenuItem>
                  ))}
                </TextField>
                <TextField select label="TVA" value={poste.tva ?? 17}
                           onChange={poser('tva')}
                           helperText="Les prix du barème sont hors TVA.">
                  {TVA.map(({ valeur, libelle }) => (
                    <MenuItem key={valeur} value={valeur}>{libelle}</MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>Montants</Typography>
                <Box sx={{ display: 'grid', gap: 2,
                           gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                  {MONTANTS.map(({ champ, libelle, aide }) => (
                    <TextField
                      key={champ}
                      label={libelle}
                      type="number"
                      inputProps={{ step: '0.01' }}
                      value={poste[champ] ?? ''}
                      onChange={poser(champ)}
                      helperText={aide}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">€</InputAdornment>
                        ),
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Analyse
                </Typography>
                <Box sx={{ '& .ql-container': { minHeight: 200 },
                           '& .ql-editor': { minHeight: 200 } }}>
                  <ReactQuill
                    theme="snow"
                    value={poste.comment || ''}
                    onChange={(html) => setPoste(
                      (etat) => ({ ...etat, comment: html }))}
                    modules={OUTILS_QUILL}
                    formats={FORMATS_QUILL}
                    placeholder="Ce que vous avez constaté, et pourquoi ce montant…"
                  />
                </Box>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onFermer} disabled={occupe}>Annuler</Button>
            <Button type="submit" variant="contained"
                    disabled={occupe || !poste.title?.trim()}>
              {occupe ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </ThemeProvider>
  );
}
